import React, { useState } from 'react';
import {
    openNotification,
    validatePassword,
    validatePhoneNumber
} from '../utils/helper';
import { AxiosError } from 'axios';
import { Field, Form, Formik, FormikHelpers } from 'formik';
import { mutateConfig } from '../api/queryBase';
import { Country } from '../interfaces/Country';
import * as Yup from 'yup';
import { useNavigate, useOutletContext } from 'react-router-dom';
import { Alert, Select } from 'antd';
import CountriesDropdown from '../components/CountriesDropdown';
import useIndustries from '../customhooks/industries/useIndustries';
import useBusinessTypes from '../customhooks/businesstypes/useBusinesstypes';
import { resendVerification } from '../api/base/base';
import Input from '../components/input/Input';
import Button from '../components/button/Button';
import { ArrowRightIcon } from '../components/icons/ArrowRightIcon';

export interface RegisterValues {
    email?: string;
    phoneNumber?: string;
    password?: string;
    rcNumber?: string;
    businessName?: string;
    countryId?: string;
    countryCode?: string;
    firstName?: string;
    lastName?: string;
    corporateIndustryId?: string;
    businessTypes?: string[];
}

export interface ResendConfirmationPayload {
    userId: number;
    corporateId: number;
    email: string;
}

interface OutletContextType {
    showConfirmation: boolean;
    setShowConfirmation: React.Dispatch<React.SetStateAction<boolean>>;
}

type ErrorMessage = {
    message: string;
};

interface ErrorResponse {
    data: string | Record<string, ErrorMessage>;
    message: string;
}

const SignUp = () => {
    const [resentConfirmationPayload, setResentConfirmationPayload] =
        useState<ResendConfirmationPayload>({} as ResendConfirmationPayload);

    const { showConfirmation, setShowConfirmation } =
        useOutletContext<OutletContextType>();

    const initialValues: RegisterValues = {
        email: undefined,
        phoneNumber: undefined,
        password: undefined,
        rcNumber: '',
        businessName: '',
        countryId: '',
        firstName: '',
        lastName: '',
        corporateIndustryId: '',
        countryCode: '',
        businessTypes: []
    };

    const [chosenCountry, setChosenCountry] = useState<Country>();
    const [genericErrorMessage, setGenericErrorMessage] = useState<string>('');
    const { industries, isIndustriesLoading } = useIndustries();
    const { businessTypes, isBusinessTypesLoading } = useBusinessTypes();
    const [isSubmitting, setIsSubmitting] = useState(false);

    const navigate = useNavigate();

    const validationSchema = Yup.object().shape({
        businessTypes: Yup.array().required('Business Type is required'),
        lastName: Yup.string().required('Last Name is required'),
        firstName: Yup.string().required('First Name is required'),
        businessName: Yup.string().required('Business Name is required'),
        email: Yup.string()
            .email('Invalid Email address')
            .required('Email is required'),
        corporateIndustryId: Yup.string().required('Industry  is required'),
        rcNumber: Yup.string().required(
            `${chosenCountry?.businessIdentification} is required`
        ),
        phoneNumber: Yup.string()
            .required('Phone Number is required')
            .test('phone-number', 'Invalid phone number', (value) =>
                validatePhoneNumber(`${chosenCountry?.phoneCode}${value}`)
            ),
        password: Yup.string()
            .required('Invalid password')
            .test('password', 'Invalid password', (value) =>
                validatePassword(value)
            )
    });

    const mutation = mutateConfig();
    const { mutate } = mutation({
        url: `/corporates/signup`
    });

    interface Payload {
        userId: number;
        corporateId: number;
        email: string;
    }

    const handleSubmit = async (
        values: RegisterValues,
        { setErrors, resetForm }: FormikHelpers<RegisterValues>
    ) => {
        setIsSubmitting(true);
        mutate(
            {
                ...values,
                corporateIndustryId: Number(values.corporateIndustryId)
            },
            {
                onSettled: (_, error: AxiosError | undefined | any) => {
                    setIsSubmitting(false);
                    if (error) {
                        if (error.response?.data) {
                            const errors = error.response.data.error;
                            if (typeof errors === 'string') {
                                setGenericErrorMessage(errors);
                            } else {
                                setErrors(errors);
                            }
                        } else {
                            openNotification(
                                'error',
                                'Sign Up, Something went wrong'
                            );
                        }
                        return;
                    }

                    resetForm();
                },
                onSuccess: (data: { payload: unknown }) => {
                    const payload = data.payload as Payload;

                    setIsSubmitting(false);
                    openNotification('success', 'Sign Up successful');
                    setShowConfirmation(true);
                    setResentConfirmationPayload({
                        userId: payload.userId,
                        corporateId: payload.corporateId,
                        email: values.email ?? ''
                    });
                },
                onError: (error: unknown) => {
                    const submitError = error as AxiosError;

                    if (!submitError?.response) {
                        openNotification(
                            'error',
                            'Sign Up: Something went wrong. Please try again.'
                        );
                        return;
                    }

                    const responseData = submitError.response
                        .data as ErrorResponse;

                    const errors = responseData?.data;

                    setGenericErrorMessage(
                        responseData?.message ||
                            'An unexpected error occurred. Please try again.'
                    );

                    if (typeof errors === 'object' && errors !== null) {
                        const formikErrors: Record<string, string> = {};

                        for (const [key, value] of Object.entries(errors)) {
                            if (
                                value &&
                                typeof value === 'object' &&
                                'message' in value
                            ) {
                                formikErrors[key] = (value as any).message;
                            } else if (Array.isArray(value)) {
                                formikErrors[key] = value;
                            } else {
                                formikErrors[key] = String(value);
                            }
                        }

                        setErrors(formikErrors);
                    }
                }
            }
        );
    };

    const [resending, setResending] = useState(false);

    const resendConfirmation = async () => {
        if (!resending) {
            setResending(true);
            try {
                await resendVerification({
                    userId: resentConfirmationPayload?.userId,
                    corporateId: resentConfirmationPayload?.corporateId
                });
                setResending(false);
                openNotification(
                    'success',
                    'Resend Confirmation, verification sent'
                );
            } catch (error) {
                setResending(false);
                openNotification(
                    'error',
                    'Resend Confirmation, Something went wrong'
                );
            }
        }
    };

    return (
        <>
            {showConfirmation ? (
                <div className='flex flex-col items-center bg-appcolorwhite pb-[2.4rem] pt-[3rem] px-4'>
                    <div>
                        <img
                            src='/img/vectors/mailbox.png'
                            alt='mailbox'
                            className='h-[74.06px] mb-[0.5rem]'
                        />
                    </div>

                    <p className='text-base mb-[0.6rem]'>
                        A confirmation has been sent to{' '}
                        <span className='text-appcolorprimary'>
                            {resentConfirmationPayload?.email}
                        </span>
                    </p>
                    <p className='text-base mb-[1.5rem]'>
                        Follow the link to complete your onboarding,
                    </p>

                    <hr className='border-dotted border-appcolorblack w-[80%] mb-[1.2rem] h-2' />

                    <p className='text-base mb-[0.6rem]'>
                        Can’t find the email?
                    </p>
                    <p
                        className='text-base mb-[2.8rem] w-fit cursor-pointer underline text-appcolorprimary'
                        onClick={() => {
                            resendConfirmation();
                        }}
                    >
                        Resend Confirmation Email
                    </p>

                    <div
                        className='flex items-center cursor-pointer w-fit'
                        onClick={() => {
                            navigate('/login');
                        }}
                    >
                        <p className='text-base text-appcolorprimary'>Login</p>
                        <div className='ml-1'>
                            <ArrowRightIcon />
                        </div>
                    </div>
                </div>
            ) : (
                <Formik
                    initialValues={initialValues}
                    validationSchema={validationSchema}
                    validateOnChange={false}
                    validateOnBlur={true}
                    onSubmit={handleSubmit}
                >
                    {({ errors, isValid, values, setFieldValue, touched }) => (
                        <Form autoComplete='off' noValidate>
                            <div className='bg-appcolorwhite py-[2.7rem] lg:py-[2.5rem] lg:px-[2.6rem] px-[1.6rem] mb-[1.75rem] rounded-[3px] text-left space-y-3'>
                                {genericErrorMessage &&
                                    typeof genericErrorMessage === 'string' && (
                                        <div className='mb-8'>
                                            <Alert
                                                type='error'
                                                message={genericErrorMessage}
                                                banner
                                                className='text-red-700 text-left'
                                            />
                                        </div>
                                    )}
                                <div className='flex gap-4 flex-col sm:flex-row'>
                                    <div className='w-full'>
                                        <Field
                                            as={Input}
                                            isError={
                                                touched?.firstName &&
                                                errors?.firstName
                                            }
                                            errorMessage={
                                                touched?.firstName &&
                                                errors?.firstName
                                            }
                                            required
                                            className='w-full'
                                            id='siginup-firstname'
                                            label='First Name'
                                            name='firstName'
                                            valid
                                        />
                                    </div>
                                    <div className='w-full'>
                                        <Field
                                            as={Input}
                                            isError={
                                                touched?.lastName &&
                                                errors?.lastName
                                            }
                                            errorMessage={
                                                touched?.lastName &&
                                                errors?.lastName
                                            }
                                            required
                                            className='w-full'
                                            id='siginup-lastName'
                                            label='Last Name'
                                            name='lastName'
                                        />
                                    </div>
                                </div>
                                <Field
                                    as={Input}
                                    isError={touched?.email && errors?.email}
                                    errorMessage={
                                        touched?.email && errors?.email
                                    }
                                    required
                                    className='w-full'
                                    id='siginup-emailAddress'
                                    label='Business Email'
                                    name='email'
                                    type='email'
                                />
                                <Field
                                    as={Input}
                                    isError={
                                        touched?.businessName &&
                                        errors?.businessName
                                    }
                                    errorMessage={
                                        touched?.businessName &&
                                        errors?.businessName
                                    }
                                    required
                                    className='w-full'
                                    id='siginup-businessName'
                                    label='Business Name'
                                    name='businessName'
                                />

                                <div>
                                    <label
                                        htmlFor='input-group-1'
                                        className='block mb-2 text-sm font-medium text-gray700 text-left'
                                    >
                                        Industry
                                    </label>
                                    <Select
                                        style={{ borderRadius: '8px' }}
                                        className='w-full h-[45px] rounded-[8px]'
                                        id='corporateIndustryId'
                                        loading={isIndustriesLoading}
                                        status={
                                            !!errors?.corporateIndustryId
                                                ? 'error'
                                                : undefined
                                        }
                                        onChange={(value) => {
                                            if (value !== null) {
                                                setFieldValue(
                                                    'corporateIndustryId',
                                                    value.toString()
                                                );
                                            }
                                        }}
                                    >
                                        {industries?.map((ind) => (
                                            <Select.Option
                                                key={`industry-${ind.id}`}
                                                value={ind.id}
                                            >
                                                {ind.name}
                                            </Select.Option>
                                        ))}
                                    </Select>
                                    {!!errors?.corporateIndustryId}
                                    <p className='text-xs text-red100'>
                                        {errors?.corporateIndustryId}
                                    </p>
                                </div>

                                <div>
                                    <label
                                        htmlFor='input-group-1'
                                        className='block mb-2 text-sm font-medium text-gray700 text-left'
                                    >
                                        Business Type
                                    </label>
                                    <Select
                                        style={{ borderRadius: '8px' }}
                                        className='w-full h-[45px] rounded-[8px]'
                                        id='businessTypes'
                                        mode='multiple'
                                        status={
                                            !!errors?.businessTypes
                                                ? 'error'
                                                : undefined
                                        }
                                        loading={isBusinessTypesLoading}
                                        onChange={(values) => {
                                            if (values !== null) {
                                                setFieldValue(
                                                    'businessTypes',
                                                    values
                                                );
                                            }
                                        }}
                                    >
                                        {businessTypes?.map((ind) => (
                                            <Select.Option
                                                key={`industry-${ind.id}`}
                                                value={ind.id}
                                            >
                                                {ind.name}
                                            </Select.Option>
                                        ))}
                                    </Select>
                                    {!!errors?.businessTypes}
                                    <p className='text-xs text-red100'>
                                        {errors?.businessTypes}
                                    </p>
                                </div>

                                <div className='flex gap-4 flex-col sm:flex-row'>
                                    <div className='w-full'>
                                        <CountriesDropdown
                                            initialValue={values.countryId}
                                            error={
                                                touched?.countryId &&
                                                !!errors?.countryId
                                            }
                                            errorMessage={
                                                touched?.countryId
                                                    ? errors?.countryId
                                                    : undefined
                                            }
                                            onHandleChange={(value) => {
                                                if (value !== null) {
                                                    setFieldValue(
                                                        'countryId',
                                                        value.id
                                                    );
                                                    setFieldValue(
                                                        'countryCode',
                                                        value.countryCode
                                                    );
                                                    setChosenCountry(value);
                                                }
                                            }}
                                            {...{
                                                errors,
                                                values
                                            }}
                                        />
                                    </div>
                                    <div className='w-full'>
                                        <Field
                                            as={Input}
                                            error={
                                                touched?.phoneNumber &&
                                                errors?.phoneNumber
                                            }
                                            helperText={
                                                touched?.phoneNumber &&
                                                errors?.phoneNumber
                                            }
                                            leftIcon={
                                                chosenCountry && (
                                                    <span className='text-appcolorprimary text-xs ml-[-5px]'>
                                                        {
                                                            chosenCountry?.phoneCode
                                                        }
                                                    </span>
                                                )
                                            }
                                            margin='normal'
                                            required
                                            className='w-full'
                                            id='siginup-phoneNumber'
                                            label='Phone Number'
                                            name='phoneNumber'
                                        />
                                    </div>
                                </div>

                                {chosenCountry && (
                                    <Field
                                        as={Input}
                                        isError={
                                            touched?.rcNumber &&
                                            errors?.rcNumber
                                        }
                                        errorMessage={
                                            touched?.rcNumber &&
                                            errors?.rcNumber
                                        }
                                        required
                                        className='w-full'
                                        id='siginup-rcNumber'
                                        name='rcNumber'
                                        label={`${chosenCountry?.businessIdentification}`}
                                    />
                                )}
                                <Input
                                    id='signup-password'
                                    label='Password'
                                    name='password'
                                    type='password'
                                    onChange={(e) => {
                                        setFieldValue(
                                            'password',
                                            e.target.value
                                        );
                                    }}
                                    isError={
                                        touched?.password && !!errors?.password
                                    }
                                    errorMessage={
                                        touched?.password
                                            ? errors?.password
                                            : ''
                                    }
                                />
                                <div className='mt-4'>
                                    <Button
                                        isLoading={isSubmitting}
                                        type='submit'
                                        className='w-full'
                                        disabled={
                                            Object.values(values).some(
                                                (item) => item === undefined
                                            ) ||
                                            !isValid ||
                                            isSubmitting
                                        }
                                        text='Create a Free Account'
                                    />
                                </div>
                                <p className='mt-4 text-sm text-appcolorfaint leading[18px]'>
                                    Already have an account?{' '}
                                    <span
                                        className='underline cursor-pointer text-appcolorprimary'
                                        onClick={() => {
                                            navigate('/login');
                                        }}
                                    >
                                        Log In
                                    </span>
                                </p>
                                <p className='mt-4 text-sm text-appcolorfaint leading[18px]'>
                                    By Signing up you agree to our{' '}
                                    <span
                                        className='underline cursor-pointer text-appcolorprimary'
                                        onClick={() => {
                                            navigate('/signup');
                                        }}
                                    >
                                        Privacy Policy & Terms
                                    </span>
                                </p>
                            </div>
                        </Form>
                    )}
                </Formik>
            )}
        </>
    );
};

export default SignUp;
